import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, MousePointer, Eye, Target, Play, Pause, Pencil, Check, X, Phone, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import apiService from './services/api';
import { Panel, LoadingSpinner } from './components';

function GoogleAdsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [editingBudget, setEditingBudget] = useState(null);
  const [budgetValue, setBudgetValue] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);

  useEffect(() => { loadData(); }, [days]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.get('/amn/metrics/google-ads/', { days });
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load Google Ads data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCampaign = async (campaign) => {
    const action = campaign.status === 'enabled' ? 'pause' : 'enable';
    setToggling(campaign.campaign_id);
    try {
      await apiService.post('/amn/metrics/google-ads/campaign-toggle/', {
        campaign_id: campaign.campaign_id,
        action,
      });
      setData(prev => ({
        ...prev,
        campaigns: prev.campaigns.map(c =>
          c.campaign_id === campaign.campaign_id
            ? { ...c, status: action === 'enable' ? 'enabled' : 'paused' }
            : c
        ),
      }));
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to update campaign');
    } finally {
      setToggling(null);
    }
  };

  const saveBudget = async (campaign) => {
    const amount = parseFloat(budgetValue);
    if (isNaN(amount) || amount < 1 || amount > 1000) {
      alert('Budget must be between $1 and $1,000');
      return;
    }
    setSavingBudget(true);
    try {
      await apiService.post('/amn/metrics/google-ads/campaign-budget/', {
        campaign_id: campaign.campaign_id,
        daily_budget: amount,
      });
      setData(prev => ({
        ...prev,
        campaigns: prev.campaigns.map(c =>
          c.campaign_id === campaign.campaign_id
            ? { ...c, daily_budget: amount }
            : c
        ),
      }));
      setEditingBudget(null);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to update budget');
    } finally {
      setSavingBudget(false);
    }
  };

  const fmt = (v) => v != null ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
  const pct = (v) => v != null ? `${Number(v).toFixed(1)}%` : '-';

  if (loading) return <div className="flex-1 flex items-center justify-center"><LoadingSpinner /></div>;
  if (error) return <div className="flex-1 flex items-center justify-center text-red-600">{error}</div>;
  if (!data) return null;

  const { account, campaigns, performance, search_terms, leads_by_campaign, leads, total_leads, period, pipeline } = data;
  const activeCampaigns = campaigns.filter(c => c.status === 'enabled');

  const CampaignToggle = ({ campaign, size = 'sm' }) => {
    const isEnabled = campaign.status === 'enabled';
    const isLoading = toggling === campaign.campaign_id;
    return (
      <button
        onClick={(e) => { e.stopPropagation(); toggleCampaign(campaign); }}
        disabled={isLoading}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
          isEnabled
            ? 'bg-green-50 text-green-700 hover:bg-red-50 hover:text-red-700'
            : 'bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-700'
        } disabled:opacity-50`}
        title={isEnabled ? 'Pause campaign' : 'Enable campaign'}
      >
        {isLoading ? (
          <span className="w-3 h-3 block border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : isEnabled ? (
          <Pause className="w-3 h-3" />
        ) : (
          <Play className="w-3 h-3" />
        )}
        {isEnabled ? 'Active' : 'Paused'}
      </button>
    );
  };

  const BudgetEditor = ({ campaign }) => {
    const isEditing = editingBudget === campaign.campaign_id;
    if (isEditing) {
      return (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <span className="text-gray-400 text-sm">$</span>
          <input
            type="number"
            value={budgetValue}
            onChange={e => setBudgetValue(e.target.value)}
            className="w-16 text-sm border border-gray-300 rounded px-1.5 py-0.5 text-right"
            min="1"
            max="1000"
            step="1"
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter') saveBudget(campaign);
              if (e.key === 'Escape') setEditingBudget(null);
            }}
          />
          <button
            onClick={() => saveBudget(campaign)}
            disabled={savingBudget}
            className="p-0.5 text-green-600 hover:text-green-800 disabled:opacity-50"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setEditingBudget(null)}
            className="p-0.5 text-gray-400 hover:text-gray-600"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      );
    }
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setEditingBudget(campaign.campaign_id);
          setBudgetValue(campaign.daily_budget || '');
        }}
        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 group"
        title="Edit daily budget"
      >
        {fmt(campaign.daily_budget)}/d
        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
    );
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-gray-600" />
            <h1 className="text-lg font-semibold text-gray-900">Google Ads</h1>
            {account.last_synced_at && (
              <span className="text-xs text-gray-400">Synced {new Date(account.last_synced_at).toLocaleDateString()}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>

        <div className="flex gap-1 mt-3 bg-gray-100 rounded-lg p-0.5 w-fit">
          {['overview', 'campaigns', 'performance', 'search_terms', 'leads'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab === 'search_terms' ? 'Search Terms' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Panel className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <DollarSign className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs text-gray-500">Total Spend</p>
                </div>
                <p className="text-xl font-bold text-gray-900">{fmt(period.spend)}</p>
              </Panel>
              <Panel className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <MousePointer className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs text-gray-500">Clicks</p>
                </div>
                <p className="text-xl font-bold text-blue-600">{period.clicks.toLocaleString()}</p>
              </Panel>
              <Panel className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Eye className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs text-gray-500">Impressions</p>
                </div>
                <p className="text-xl font-bold text-gray-700">{period.impressions.toLocaleString()}</p>
              </Panel>
              <Panel className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Target className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs text-gray-500">Leads</p>
                </div>
                <p className="text-xl font-bold text-green-600">{total_leads}</p>
              </Panel>
              <Panel className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <DollarSign className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs text-gray-500">Avg CPC</p>
                </div>
                <p className="text-xl font-bold text-gray-700">{fmt(period.avg_cpc)}</p>
              </Panel>
              <Panel className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Target className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs text-gray-500">Cost/Lead</p>
                </div>
                <p className="text-xl font-bold text-red-600">{period.cost_per_lead ? fmt(period.cost_per_lead) : '-'}</p>
              </Panel>
            </div>

            {/* Daily Performance Chart */}
            {performance.length > 0 && (
              <Panel>
                <div className="px-4 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">Daily Performance</h3>
                </div>
                <div className="p-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={performance}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={d => new Date(d + 'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tickFormatter={v => `$${v}`} tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v, name) => [name === 'cost' ? `$${v.toFixed(2)}` : v, name]} />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="cost" stroke="#991b1b" strokeWidth={2} dot={{ r: 3 }} name="Spend" />
                      <Line yAxisId="right" type="monotone" dataKey="clicks" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} name="Clicks" />
                      <Line yAxisId="right" type="monotone" dataKey="impressions" stroke="#9ca3af" strokeWidth={1} dot={false} name="Impressions" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            )}

            {/* Campaigns Summary with Controls */}
            <Panel>
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Campaigns ({campaigns.length})</h3>
                <span className="text-xs text-gray-400">{activeCampaigns.length} active</span>
              </div>
              <div className="divide-y divide-gray-100">
                {campaigns.map(c => (
                  <div key={c.campaign_id} className={`px-4 py-3 flex items-center justify-between ${c.status !== 'enabled' ? 'opacity-60' : ''}`}>
                    <div className="flex items-center gap-3">
                      <CampaignToggle campaign={c} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{c.name}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <BudgetEditor campaign={c} />
                          <span>·</span>
                          <span>{c.bidding_strategy}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-600">{fmt(c.period_cost)}</span>
                      <span className="text-blue-600">{c.period_clicks} clicks</span>
                      <span className="text-green-600">{c.leads || 0} leads</span>
                      {c.cost_per_lead && <span className="text-red-600 font-medium">{fmt(c.cost_per_lead)} CPL</span>}
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          </>
        )}

        {/* CAMPAIGNS TAB */}
        {activeTab === 'campaigns' && (
          <Panel>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left text-xs font-medium text-gray-500 px-4 py-2.5">Campaign</th>
                    <th className="text-center text-xs font-medium text-gray-500 px-3 py-2.5">Status</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2.5">Budget</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2.5">Spend</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2.5">Impressions</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2.5">Clicks</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2.5">CTR</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2.5">Avg CPC</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2.5">Leads</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2.5">Cost/Lead</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {campaigns.map(c => (
                    <tr key={c.campaign_id} className={`hover:bg-gray-50 ${c.status !== 'enabled' ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-2.5">
                        <p className="text-sm font-medium text-gray-900">{c.name}</p>
                        <p className="text-xs text-gray-400">Started {c.start_date}</p>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <CampaignToggle campaign={c} />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <BudgetEditor campaign={c} />
                      </td>
                      <td className="px-3 py-2.5 text-sm text-right font-medium text-gray-900">{fmt(c.period_cost)}</td>
                      <td className="px-3 py-2.5 text-sm text-right text-gray-600">{(c.period_impressions || 0).toLocaleString()}</td>
                      <td className="px-3 py-2.5 text-sm text-right text-blue-600">{c.period_clicks || 0}</td>
                      <td className="px-3 py-2.5 text-sm text-right text-gray-600">{c.period_clicks && c.period_impressions ? pct(c.period_clicks / c.period_impressions * 100) : '-'}</td>
                      <td className="px-3 py-2.5 text-sm text-right text-gray-600">{c.period_clicks ? fmt(c.period_cost / c.period_clicks) : '-'}</td>
                      <td className="px-3 py-2.5 text-sm text-right font-medium text-green-600">{c.leads || 0}</td>
                      <td className="px-3 py-2.5 text-sm text-right font-medium text-red-600">{c.cost_per_lead ? fmt(c.cost_per_lead) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        {/* PERFORMANCE TAB */}
        {activeTab === 'performance' && (
          <>
            <Panel>
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">Spend & Clicks Over Time</h3>
              </div>
              <div className="p-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={performance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={d => new Date(d + 'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tickFormatter={v => `$${v}`} tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v, name) => [name === 'Spend' ? `$${Number(v).toFixed(2)}` : v, name]} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="cost" fill="#991b1b" radius={[2,2,0,0]} name="Spend" />
                    <Bar yAxisId="right" dataKey="clicks" fill="#2563eb" radius={[2,2,0,0]} name="Clicks" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel>
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">Daily Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left text-xs font-medium text-gray-500 px-4 py-2">Date</th>
                      <th className="text-right text-xs font-medium text-gray-500 px-3 py-2">Impressions</th>
                      <th className="text-right text-xs font-medium text-gray-500 px-3 py-2">Clicks</th>
                      <th className="text-right text-xs font-medium text-gray-500 px-3 py-2">Spend</th>
                      <th className="text-right text-xs font-medium text-gray-500 px-3 py-2">Conversions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[...performance].reverse().map(p => (
                      <tr key={p.date} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-900">{new Date(p.date + 'T12:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                        <td className="px-3 py-2 text-sm text-right text-gray-600">{p.impressions.toLocaleString()}</td>
                        <td className="px-3 py-2 text-sm text-right text-blue-600">{p.clicks}</td>
                        <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">{fmt(p.cost)}</td>
                        <td className="px-3 py-2 text-sm text-right text-green-600">{p.conversions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </>
        )}

        {/* SEARCH TERMS TAB */}
        {activeTab === 'search_terms' && (
          <Panel>
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Search Terms ({search_terms.length})</h3>
                <p className="text-xs text-gray-400">What people actually searched for</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left text-xs font-medium text-gray-500 px-4 py-2">Search Term</th>
                    <th className="text-left text-xs font-medium text-gray-500 px-3 py-2">Campaign</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2">Impressions</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2">Clicks</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2">Cost</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2">CTR</th>
                    <th className="text-right text-xs font-medium text-gray-500 px-3 py-2">Avg CPC</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {search_terms.sort((a, b) => b.clicks - a.clicks).map((st, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{st.search_term}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{st.campaign_name}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-600">{st.impressions}</td>
                      <td className="px-3 py-2 text-sm text-right text-blue-600">{st.clicks}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-900">{fmt(st.cost)}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-600">{pct(st.ctr)}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-600">{fmt(st.average_cpc)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        {/* LEADS TAB */}
        {activeTab === 'leads' && (
          <>
            {/* Pipeline */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Panel className="p-3 border-l-4 border-l-gray-400">
                <p className="text-xs text-gray-500">Total Leads</p>
                <p className="text-2xl font-bold text-gray-900">{total_leads}</p>
              </Panel>
              <Panel className="p-3 border-l-4 border-l-blue-400">
                <p className="text-xs text-gray-500">New</p>
                <p className="text-2xl font-bold text-blue-600">{(pipeline?.new || 0) + (pipeline?.other || 0)}</p>
              </Panel>
              <Panel className="p-3 border-l-4 border-l-amber-400">
                <p className="text-xs text-gray-500">Follow Up</p>
                <p className="text-2xl font-bold text-amber-600">{pipeline?.follow_up || 0}</p>
              </Panel>
              <Panel className="p-3 border-l-4 border-l-green-500">
                <p className="text-xs text-gray-500">Won</p>
                <p className="text-2xl font-bold text-green-600">{pipeline?.converted || 0}</p>
              </Panel>
              <Panel className="p-3 border-l-4 border-l-red-400">
                <p className="text-xs text-gray-500">Lost</p>
                <p className="text-2xl font-bold text-red-500">{pipeline?.lost || 0}</p>
              </Panel>
            </div>

            {/* Revenue Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Panel className="p-3">
                <p className="text-xs text-gray-500">Ad Spend</p>
                <p className="text-2xl font-bold text-gray-900">{fmt(period.spend)}</p>
              </Panel>
              <Panel className="p-3">
                <p className="text-xs text-gray-500">Cost per Lead</p>
                <p className="text-2xl font-bold text-red-600">{period.cost_per_lead ? fmt(period.cost_per_lead) : '-'}</p>
              </Panel>
              <Panel className="p-3">
                <p className="text-xs text-gray-500">Revenue from Ads</p>
                <p className="text-2xl font-bold text-green-600">{fmt(period.converted_revenue)}</p>
              </Panel>
              <Panel className="p-3">
                <p className="text-xs text-gray-500">ROAS</p>
                <p className="text-2xl font-bold text-blue-600">{period.roas ? `${period.roas}x` : '-'}</p>
              </Panel>
            </div>

            {/* Lead Table */}
            <Panel>
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">All Leads ({total_leads})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left text-xs font-medium text-gray-500 px-4 py-2">Date</th>
                      <th className="text-left text-xs font-medium text-gray-500 px-3 py-2">Name</th>
                      <th className="text-left text-xs font-medium text-gray-500 px-3 py-2">Phone</th>
                      <th className="text-left text-xs font-medium text-gray-500 px-3 py-2">Campaign</th>
                      <th className="text-left text-xs font-medium text-gray-500 px-3 py-2">Keyword</th>
                      <th className="text-center text-xs font-medium text-gray-500 px-3 py-2">Status</th>
                      <th className="text-right text-xs font-medium text-gray-500 px-3 py-2">Appts</th>
                      <th className="text-right text-xs font-medium text-gray-500 px-3 py-2">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(leads || []).length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-8 text-sm text-gray-400">
                          No UTM-tracked leads yet. Leads will appear here once form submissions or phone calls come in via Google Ads.
                        </td>
                      </tr>
                    ) : (
                      (leads || []).map(l => (
                        <tr
                          key={l.id}
                          className={`hover:bg-gray-50 cursor-pointer ${l.converted ? 'bg-green-50/50' : ''}`}
                          onClick={() => l.client_id && navigate(`/clients/${l.client_id}`)}
                        >
                          <td className="px-4 py-2 text-sm text-gray-900">{l.date ? new Date(l.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}</td>
                          <td className="px-3 py-2 text-sm font-medium text-gray-900">{l.name}</td>
                          <td className="px-3 py-2 text-sm text-gray-600">{l.phone || '-'}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{l.campaign}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{l.keyword || '-'}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                              l.status === 'converted' ? 'bg-green-100 text-green-700'
                              : l.status === 'follow_up' ? 'bg-amber-100 text-amber-700'
                              : l.status === 'lost' ? 'bg-red-100 text-red-600'
                              : l.status === 'snoozed' ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                            }`}>
                              {l.status === 'converted' ? 'Won' : l.status === 'follow_up' ? 'Follow Up' : l.status === 'new' ? 'New' : l.status === 'lost' ? 'Lost' : l.status === 'snoozed' ? 'Snoozed' : (l.status || 'New').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm text-right text-gray-600">{l.appointments || 0}</td>
                          <td className="px-3 py-2 text-sm text-right font-medium text-green-600">{l.revenue > 0 ? fmt(l.revenue) : '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>

            {/* Leads by Campaign */}
            <Panel>
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">By Campaign</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {Object.entries(leads_by_campaign).sort((a, b) => b[1] - a[1]).map(([campaign, count]) => {
                  const c = campaigns.find(x => x.name === campaign);
                  const cost = c ? c.period_cost : 0;
                  return (
                    <div key={campaign} className="px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{campaign}</p>
                        {c && <p className="text-xs text-gray-400">{fmt(cost)} spend · {c.period_clicks} clicks</p>}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="font-semibold text-green-600">{count} leads</span>
                        {cost > 0 && count > 0 && (
                          <span className="text-red-600">{fmt(cost / count)} CPL</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          </>
        )}
      </div>
    </div>
  );
}

export default GoogleAdsPage;
